import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Activity, Clock, Search } from 'lucide-react'
import DataTable from '../../components/DataTable'
import { useOnChange, useRemembered } from '../../components/listMemory'
import { usePageParam } from '../../components/useListParams'
import FilterBar from '../../components/FilterBar'
import PageHeader from '../../components/PageHeader'
import StatStrip from '../../components/StatStrip'
import { useToast } from '../../components/toastContext'
import { listActivities } from './systemLogsService'
import { systemStateStrings as S } from './strings'
import './systemLogs.css'
import { serverDetail } from '../../lib/i18n'

/**
 * سجل النظام — the legacy `SystemState`.
 *
 * SERVER-PAGED, and it has to be: 5,682 activities here, one more on every login, and the
 * endpoint clamps a page to 100 rows — there is no way to ask for the whole table.
 *
 * The order is the server's, asked for by column name: `GetActivitiesHandler` reads the DataTables
 * sort and opens on latest first. The row number is a position in the page, not a column.
 */
export default function SystemStatePage() {
  const toast = useToast()

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [everything, setEverything] = useState(0)
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one: without this the table renders its "no records"
     state for a database fault, which tells the operator the opposite of the truth. */
  const [loadError, setLoadError] = useState(null)
  /* The search decides WHICH rows the remembered page holds, and it used to be dropped on
     the way out: the address came back saying `?page=7` over the whole log instead of over
     the seven pages of "ahmed" the reader had left. */
  const [query, setQuery] = useRemembered('system-state:query', '')
  /* In the address, so Back and a refresh both land on the same page. */
  const [page, setPage] = usePageParam()
  /* Remembered for the same reason: a page of 100 chosen deliberately snapped back to 25. */
  const [pageSize, setPageSize] = useRemembered('system-state:pageSize', 25)
  /* The order, owned here with the page; the default is the handler's own, latest first. */
  const [sort, setSort] = useRemembered('system-state:sort', { key: 'activityDate', dir: 'desc' })

  /* A new search is a new list — on a CHANGE, not on arrival, which would throw away
     the page this screen was left on. */
  useOnChange(() => setPage(1), [query])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listActivities({ page, pageSize, query, sort })
      .then((response) => {
        if (cancelled) return
        setLoadError(null)
        setRows(response.rows)
        setTotal(response.total)
        setEverything(response.totalUnfiltered)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoadError(error)
        setRows([])
        setTotal(0)
        setLoading(false)
        toast.error(serverDetail(error?.message) || S.fail.list)
      })
    return () => {
      cancelled = true
    }
  }, [page, pageSize, query, sort, toast])

  /*
   * Two figures, and only two, because only two can be true.
   *
   * This strip used to count distinct people and the commonest activity across the rows the page
   * held — fine over a mock that returned everything, a lie the moment the list is server-paged,
   * because it would then describe the twenty-five rows on screen and call them the system. There
   * is no aggregate endpoint to replace them with, so they are gone rather than wrong; see
   * COSMETIC_UI.md.
   *
   * What survives is what the response actually says: how many activities exist, and — because
   * the order is newest-first and fixed — the newest one, which is row 1 of page 1.
   */
  const summary = useMemo(() => {
    const newest = page === 1 && !query ? rows[0] : null
    return [
      { id: 'total', icon: Activity, label: S.summary.total, value: everything },
      {
        id: 'latest',
        icon: Clock,
        label: S.summary.latest,
        value: newest ? newest.activityDate : '—',
        hint: newest ? newest.activity : undefined,
      },
    ]
  }, [rows, everything, page, query])

  const columns = useMemo(
    () => [
      {
        key: 'index',
        header: S.columns.index,
        width: '64px',
        sortable: false,
        searchable: false,
        /* Numbered here rather than from the row. `ActivityDto._1` is meant to be the row number
           and is not: `Start ?? 0 + index + 1` binds as `Start ?? (0 + index + 1)` and `Start` is
           never null, so every row on a page carries the same figure — 0, then 25, then 50.
           `index` is already absolute — DataTable passes `start + index` — so the page offset
           must NOT be added again here. */
        render: (_row, index) => <span className="sl-index">{index + 1}</span>,
      },
      {
        key: 'username',
        header: S.columns.username,
        /* The API wraps this in an anchor to `{DomainUrl}/UsersDetails?UserId=…` — an external
           host, and a path that is not a route in this product. The name and the id are taken and
           the link rebuilt against the screen that does exist. */
        render: (row) =>
          row.userId ? (
            <Link className="sl-user" to={`/users/${row.userId}`} dir="ltr">
              {row.username}
            </Link>
          ) : (
            <span dir="ltr">{row.username}</span>
          ),
      },
      {
        key: 'activity',
        header: S.columns.activity,
        render: (row) => <bdi>{row.activity}</bdi>,
      },
      {
        key: 'activityDate',
        header: S.columns.activityDate,
        width: '200px',
        render: (row) => (
          <span className="sl-date" dir="ltr">
            {row.activityDate || '—'}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <>
      {/* NO FIGURES WHEN THE LOAD FAILED. `SystemLogsManagers` reaches this screen but
          `/SystemState/GetActivities` requires `AdvancedSettingsManagers`, so the call 403s —
          and the page went on to report "0" activities and a "—" latest entry beside a
          dismissible dialog. Once the dialog is gone the screen is indistinguishable from an
          installation that has never recorded anything, which is a claim the app cannot make
          about a log it was refused. The table's own error state is what remains. */}
      <PageHeader
        title={S.title}
        count={loading || loadError ? undefined : total}
      />

      {!loadError && <StatStrip items={summary} label={S.summary.caption} loading={loading} />}

      {/* Searched on the server, over the WHOLE log — matching a username or an activity name.
          A local box would only search the page on screen. */}
      {!loadError && (
        <FilterBar
          search={{ value: query, onChange: setQuery, placeholder: S.searchPlaceholder }}
          count={query && !loading ? `${total.toLocaleString('en-US')}` : undefined}
        />
      )}

      <DataTable
        error={loadError}
        columns={columns}
        rows={rows}
        loading={loading}
        query={query}
        emptyMessage={S.empty}
        getRowId={(row) => row.id}
        server={{
          page,
          pageSize,
          total,
          sort,
          onChange: (next) => {
            setPage(next.page)
            setPageSize(next.pageSize)
            setSort(next.sort)
          },
        }}
      />

      {/* The export buttons that were here are gone. `/SystemState/Export` — the URL the legacy
          screen links to — is not a route in this API, so those buttons were dead there too, and
          the endpoint that feeds this table caps a request at 100 rows, so there is nothing
          honest to build client-side out of 5,682. Registered in COSMETIC_UI.md with the exact
          route it needs. */}
      {!loading && (
        <p className="sl-note">
          <Search size={14} strokeWidth={1.75} aria-hidden="true" />
          {S.exportNote}
        </p>
      )}
    </>
  )
}
